// src/services/commentService.ts
import { activeProfileHeader } from './viewerProfileService';
import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

export interface Comment {
  id: string;
  user: {
    id: string;
    username: string;
    first_name?: string;
    last_name?: string;
    firstName?: string;  // camelCase alternative
    lastName?: string;   // camelCase alternative
    fullName?: string;   // full name from backend
    avatar_url?: string;
    avatarUrl?: string;  // camelCase alternative
    is_admin?: boolean;
  };
  content_type: string;
  object_id: string;
  text: string;
  parent?: string;
  status: 'published' | 'pending' | 'hidden' | 'deleted';
  like_count: number;
  likeCount?: number;    // camelCase alternative from backend
  dislike_count: number;
  reply_count: number;
  replyCount?: number;   // camelCase alternative from backend
  is_edited: boolean;
  edited_at?: string;
  is_flagged: boolean;
  created_at?: string;  // snake_case
  createdAt?: string;   // camelCase alternative
  updated_at?: string;  // snake_case
  updatedAt?: string;   // camelCase alternative
  replies?: Comment[];
  user_interaction?: {
    liked: boolean;
    disliked: boolean;
  };
  userInteraction?: {  // camelCase alternative from backend
    liked: boolean;
    disliked: boolean;
  };
  time_since?: string;
  thread_level?: number;
  can_edit?: boolean;
  can_delete?: boolean;
}

export interface CreateCommentData {
  contentType: string; // e.g., 'cinema', 'content', 'podcast', etc.
  contentId: string;
  text: string;
  parentId?: string;
}

export interface CommentFilters {
  content_type?: string;
  object_id?: string;
  status?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

// Enhanced Comment interface for moderation
export interface ModerationComment extends Comment {
  moderated_by?: {
    username: string;
  };
  moderatedBy?: {  // camelCase alternative
    username: string;
  };
  moderated_at?: string;
  moderatedAt?: string;  // camelCase alternative
  moderation_reason?: string;
  moderationReason?: string;  // camelCase alternative
  content_title: string;
  contentTitle?: string;  // camelCase alternative
  report_count: number;
  reportCount?: number;  // camelCase alternative
  flagged_by_users?: Array<{
    id: string;
    username: string;
  }>;
  moderation_history?: Array<{
    action: string;
    moderator?: string;
    reason: string;
    old_status: string;
    new_status: string;
    created_at: string;
  }>;
  risk_score: number;
  riskScore?: number;  // camelCase alternative
}

export interface CommentModerationStats {
  totalComments: number;
  publishedComments: number;
  pendingComments: number;
  hiddenComments: number;
  deletedComments: number;
  flaggedComments: number;
  recentReports: number;
  moderationActionsToday: number;
  top_moderators?: Array<{
    moderator__username: string;
    action_count: number;
  }>;
  actions_by_type?: Array<{
    action: string;
    count: number;
  }>;
}

interface SpringBootPageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  size: number;
  number: number;
}

export interface BulkModerationData {
  comment_ids: string[];
  action: 'approve' | 'hide' | 'delete' | 'flag' | 'unflag';
  reason?: string;
}

class CommentService {
  private getAuthHeaders() {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Authorization is attached by unifiedAuth.fetch, which refreshes on expiry.
    
    return headers;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}/comments${endpoint}`;

    // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch();
    // the fallback-to-unauthenticated-GET logic below is this service's own.
    // Which viewer on the account is asking. A profile capped below the account cannot
    // read or post comments on something it cannot watch, and the server decides that -
    // but only if it is told which profile. Absent means the account's own check.
    const response = await unifiedAuth.fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...activeProfileHeader(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // If we get a 401 (Unauthorized) and we have a token, it might be invalid
      if (response.status === 401 && unifiedAuth.getAccessToken()) {
        unifiedAuth.clearTokens();
        
        // Retry the request without authentication for read operations
        if (options.method === 'GET' || !options.method) {
          // No profile header here on purpose: the token has just been cleared, so
          // there is no account for a profile to belong to and the server would ignore
          // it anyway.
          const retryResponse = await unifiedAuth.fetch(url, {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              ...options.headers,
            },
          });
          
          if (retryResponse.ok) {
            if (retryResponse.status === 204 || retryResponse.headers.get('content-length') === '0') {
              return {} as T;
            }
            
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return await retryResponse.json();
            }
            return {} as T;
          }
        }
      }
      
      throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content from DELETE requests)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    // For non-JSON responses, return empty object
    return {} as T;
  }

  // Get comments for content
  async getComments(filters: CommentFilters = {}, signal?: AbortSignal): Promise<{
    count: number;
    next: string | null;
    previous: string | null;
    results: Comment[];
  }> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return this.makeRequest(`/${queryString ? `?${queryString}` : ''}`, { signal });
  }

  // Get comments for specific content (uses the specific backend endpoint)
  async getContentComments(
    contentId: string,
    contentType: 'cinema' | 'content' | 'podcast' | 'animation' | 'sneak-peek' | 'live-video',
    page = 0,  // Backend uses 0-based page indexing
    pageSize = 10, 
    signal?: AbortSignal
  ): Promise<{
    count: number;
    next: string | null;
    previous: string | null;
    results: Comment[];
  }> {
    if (!contentId || contentId === 'undefined') {
      throw new Error(`Invalid value '${contentId}' for parameter 'contentId'`);
    }
    
    if (!contentType) {
      throw new Error(`Invalid value '${contentType}' for parameter 'contentType'`);
    }
    
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', pageSize.toString());
    
    const queryString = params.toString();
    const response = await this.makeRequest(`/content/${contentType}/${contentId}?${queryString}`, { signal }) as SpringBootPageResponse<Comment>;
    
    // Backend returns Spring Boot Page format, transform to expected format
    return {
      count: response.totalElements || 0,
      next: response.last ? null : 'next',
      previous: response.first ? null : 'previous',
      results: response.content || []
    };
  }

  // Get single comment
  async getComment(id: string): Promise<Comment> {
    return this.makeRequest(`/${id}/`);
  }

  // Create comment
  async createComment(data: CreateCommentData): Promise<Comment> {
    return this.makeRequest('', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Create original comment
  async createOriginalComment(originalId: string, text: string, parentId?: string): Promise<Comment> {
    return this.createComment({
      contentType: 'original',
      contentId: originalId,
      text,
      parentId: parentId,
    });
  }

  // Get comments for a specific podcast
  async getPodcastComments(
    podcastId: string, 
    page = 1, 
    pageSize = 20, 
    signal?: AbortSignal
  ): Promise<{
    count: number;
    next: string | null;
    previous: string | null;
    results: Comment[];
  }> {
    // Use the same pattern as getContentComments but for podcasts
    if (!podcastId || podcastId === 'undefined') {
      throw new Error(`Invalid value '${podcastId}' for parameter 'podcastId'`);
    }
    
    // Convert to 0-based page indexing for backend
    const backendPage = Math.max(0, page - 1);
    
    const params = new URLSearchParams();
    params.append('page', backendPage.toString());
    params.append('size', pageSize.toString());
    
    const queryString = params.toString();
    const response = await this.makeRequest(`/content/podcast/${podcastId}?${queryString}`, { signal }) as SpringBootPageResponse<Comment>;
    
    // Backend returns Spring Boot Page format, transform to expected format
    return {
      count: response.totalElements || 0,
      next: response.last ? null : 'next',
      previous: response.first ? null : 'previous',
      results: response.content || []
    };
  }

  // Update comment
  async updateComment(id: string, text: string): Promise<Comment> {
    return this.makeRequest(`/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ text }),
    });
  }

  // Delete comment
  async deleteComment(id: string): Promise<void> {
    await this.makeRequest(`/${id}`, {
      method: 'DELETE',
    });
  }

  // Toggle like/unlike comment (backend automatically toggles)
  async likeComment(commentId: string): Promise<{ message: string }> {
    return this.makeRequest(`/${commentId}/like`, {
      method: 'POST',
    });
  }

  // Legacy method for compatibility
  async interactWithComment(
    commentId: string, 
    action: 'like' | 'dislike' | 'report'
  ): Promise<{ message: string; action: string }> {
    if (action === 'like') {
      const result = await this.likeComment(commentId);
      return { ...result, action };
    }
    // For other actions, implement as needed
    throw new Error(`Action ${action} not implemented yet`);
  }

  // Get user's comments
  async getMyComments(page = 1, pageSize = 20): Promise<{
    count: number;
    next: string | null;
    previous: string | null;
    results: Comment[];
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    return this.makeRequest(`/my-comments/?${params.toString()}`);
  }

  // Get comment notifications
  async getNotifications(): Promise<{
    unread_count: number;
    notifications: Array<{
      id: string;
      type: string;
      message: string;
      comment: Comment;
      is_read: boolean;
      created_at: string;
    }>;
  }> {
    return this.makeRequest('/notifications/');
  }

  // Mark notification as read
  async markNotificationRead(notificationId: string): Promise<void> {
    await this.makeRequest(`/notifications/${notificationId}/read/`, {
      method: 'POST',
    });
  }

  // Get comment statistics
  async getStats(): Promise<{
    total_comments: number;
    comments_today: number;
    top_commented_originals: Array<{
      original_id: string;
      original_title: string;
      comment_count: number;
    }>;
    recent_comments: Comment[];
  }> {
    return this.makeRequest('/stats/');
  }

  // === MODERATION METHODS ===

  // Get comments for moderation (via admin endpoints)
  async getModerationComments(filters: {
    status?: string;
    flagged?: boolean;
    content_type?: string;
    search?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<{
    results: ModerationComment[];
    count: number;
    next?: string;
    previous?: string;
  }> {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status); // Always send status if provided
    if (filters.flagged) params.append('flagged', 'true');
    if (filters.content_type) params.append('content_type', filters.content_type);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', (filters.page - 1).toString()); // Convert to 0-based indexing
    if (filters.page_size) params.append('size', filters.page_size.toString());

    try {
      // Use admin endpoint for moderation
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/admin/moderation/?${params.toString()}`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Transform backend response to match expected format
      return {
        results: data.data?.results || [],
        count: data.data?.count || 0,
        next: data.data?.page < data.data?.total_pages ? 'next' : undefined,
        previous: data.data?.page > 1 ? 'previous' : undefined
      };
    } catch (_error) {
      // Return empty results if backend is not available
      return {
        results: [],
        count: 0,
        next: undefined,
        previous: undefined
      };
    }
  }

  // Get moderation statistics (via admin endpoints)
  async getModerationStats(days = 30): Promise<CommentModerationStats> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/admin/moderation/stats/?days=${days}`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Extract data from ApiResponse wrapper if it exists
      if (data.data) {
        return data.data;
      }
      
      return data;
    } catch (_error) {
      // Return demo data if backend is not available
      return {
        totalComments: 0,
        publishedComments: 0,
        pendingComments: 0,
        hiddenComments: 0,
        deletedComments: 0,
        flaggedComments: 0,
        recentReports: 0,
        moderationActionsToday: 0
      };
    }
  }

  // Bulk moderate comments (via admin endpoints)
  async bulkModerateComments(data: BulkModerationData): Promise<{
    message: string;
    moderated_count: number;
  }> {
    const response = await unifiedAuth.fetch(`${API_BASE_URL}/admin/moderation/bulk/`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        commentIds: data.comment_ids,
        action: data.action,
        reason: data.reason
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Auto-moderate comments
  async autoModerateComments(): Promise<{
    message: string;
    flagged_count: number;
  }> {
    return this.makeRequest('/moderation/auto/', {
      method: 'POST',
    });
  }

  // Moderate individual comment (via admin endpoints)
  async moderateComment(
    commentId: string, 
    action: 'approve' | 'hide' | 'delete', 
    reason?: string
  ): Promise<{
    message: string;
    comment: ModerationComment;
  }> {
    const response = await unifiedAuth.fetch(`${API_BASE_URL}/admin/moderation/comments/${commentId}/moderate/`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, reason }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Hard delete comment (permanent)
  async hardDeleteComment(
    commentId: string, 
    reason: string
  ): Promise<{
    message: string;
    deleted_comment: {
      id: string;
      user: string;
      text: string;
    };
  }> {
    return this.makeRequest(`/${commentId}/hard-delete/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
    });
  }

  // Flag comment
  async flagComment(commentId: string, reason?: string): Promise<ModerationComment> {
    const response = await unifiedAuth.fetch(`${API_BASE_URL}/admin/comments/${commentId}/flag/`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason: reason || 'Inappropriate content' }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Unflag comment (admin only)
  async unflagComment(commentId: string): Promise<ModerationComment> {
    const response = await unifiedAuth.fetch(`${API_BASE_URL}/admin/comments/${commentId}/unflag/`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Get flagged comments
  async getFlaggedComments(filters: {
    page?: number;
    page_size?: number;
  } = {}): Promise<{
    results: ModerationComment[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', (filters.page - 1).toString()); // Convert to 0-based
    if (filters.page_size) params.append('size', filters.page_size.toString());

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/admin/moderation/flagged/?${params}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  }
}

export const commentService = new CommentService();
export default commentService;
